import React from 'react';
import {
  Box,
  IconButton,
  Tooltip,
  Badge,
  Menu,
  MenuItem,
  Typography,
  Divider,
  Chip,
  ListItemText,
} from '@mui/material';
import {
  Search,
  NotificationsNone,
  FactCheck,
  AutoAwesome,
  DoneAll,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationService, approvalService, NotificationItem } from '../../services/workspaceService';
import CommandPalette, { useCommandPalette } from '../../command/CommandPalette';
import AssistantPanel, { useAssistantAvailable } from '../../ai/AssistantPanel';

const SEVERITY_ORDER = ['CRITICAL', 'IMPORTANT', 'NORMAL', 'INFO'];

/**
 * Header cluster: command palette, approvals queue, notifications, assistant.
 *
 * Kept out of Header.tsx so the header stays a layout component and this stays
 * the place that talks to the workspace APIs.
 */
const HeaderActions: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { open: paletteOpen, setOpen: setPaletteOpen } = useCommandPalette();
  const [assistantOpen, setAssistantOpen] = React.useState(false);
  const [notifAnchor, setNotifAnchor] = React.useState<null | HTMLElement>(null);
  const assistantAvailable = useAssistantAvailable();

  // Polled rather than pushed — there is no WebSocket infrastructure, and a
  // 60s poll on two small counts is honest about that rather than pretending
  // to be realtime.
  const { data: unread } = useQuery<{ total: number; bySeverity: Record<string, number> }>({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => notificationService.unreadCount(),
    refetchInterval: 60_000,
    retry: false,
  });

  const { data: approvals } = useQuery<{ summary: Array<{ entity_type: string; count: number }>; total: number }>({
    queryKey: ['approvals', 'summary'],
    queryFn: () => approvalService.summary(),
    refetchInterval: 60_000,
    retry: false,
  });

  const { data: notifList } = useQuery<any>({
    queryKey: ['notifications', 'list'],
    queryFn: () => notificationService.list({ unreadOnly: true, limit: 8 }),
    enabled: Boolean(notifAnchor),
    retry: false,
  });

  const markAll = useMutation({
    mutationFn: () => notificationService.markAllRead(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });

  const notifications: NotificationItem[] = (notifList as any)?.notifications ?? [];
  const unreadTotal = unread?.total ?? 0;
  const approvalTotal = approvals?.total ?? 0;

  const sorted = [...notifications].sort(
    (a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity)
  );

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        {/* A real search affordance rather than a bare icon. It is a button,
            not an input — focus goes straight into the palette, so there is
            never a second field that behaves subtly differently. */}
        <Box
          role="button"
          tabIndex={0}
          onClick={() => setPaletteOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setPaletteOpen(true);
            }
          }}
          aria-label="Search anything"
          sx={{
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 1,
            width: 260,
            mr: 1,
            px: 1.5,
            py: 0.75,
            borderRadius: 2,
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.75)',
            backgroundColor: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.14)',
            transition: 'background-color .15s ease',
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.16)' },
            '&:focus-visible': { outline: '2px solid rgba(255,255,255,0.6)', outlineOffset: 2 },
          }}
        >
          <Search sx={{ fontSize: 18 }} />
          <Typography variant="body2" sx={{ flexGrow: 1, fontSize: '0.8125rem' }}>
            Search anything…
          </Typography>
          <Box
            component="kbd"
            sx={{
              fontSize: '0.6875rem',
              fontFamily: 'inherit',
              px: 0.625,
              py: 0.125,
              borderRadius: 1,
              border: '1px solid rgba(255,255,255,0.25)',
              color: 'rgba(255,255,255,0.7)',
            }}
          >
            {navigator.platform.toLowerCase().includes('mac') ? '⌘K' : 'Ctrl K'}
          </Box>
        </Box>

        {/* Icon-only fallback below md, where the bar would crowd the toolbar. */}
        <Tooltip title="Search  ·  Ctrl+K">
          <IconButton
            color="inherit"
            onClick={() => setPaletteOpen(true)}
            aria-label="open command palette"
            sx={{ display: { xs: 'inline-flex', md: 'none' } }}
          >
            <Search fontSize="small" />
          </IconButton>
        </Tooltip>

        {approvalTotal > 0 && (
          <Tooltip title={`${approvalTotal} awaiting your approval`}>
            <IconButton color="inherit" onClick={() => navigate('/approvals/pending')} aria-label="approval queue">
              <Badge badgeContent={approvalTotal} color="warning" max={99}>
                <FactCheck fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>
        )}

        <Tooltip title="Notifications">
          <IconButton
            color="inherit"
            onClick={(e) => setNotifAnchor(e.currentTarget)}
            aria-label={`notifications${unreadTotal ? `, ${unreadTotal} unread` : ''}`}
          >
            <Badge badgeContent={unreadTotal} color="error" max={99}>
              <NotificationsNone fontSize="small" />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* No AI credentials -> no entry point at all. A button that always
            errors is worse than no button. */}
        {assistantAvailable && (
          <Tooltip title="Assistant">
            <IconButton color="inherit" onClick={() => setAssistantOpen(true)} aria-label="open assistant">
              <AutoAwesome fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Menu
        anchorEl={notifAnchor}
        open={Boolean(notifAnchor)}
        onClose={() => setNotifAnchor(null)}
        PaperProps={{ sx: { width: 360, maxHeight: 460 } }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.25 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1 }}>
            Notifications
          </Typography>
          {unreadTotal > 0 && (
            <Tooltip title="Mark all read">
              <IconButton size="small" onClick={() => markAll.mutate()}>
                <DoneAll fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        <Divider />

        {sorted.length === 0 && (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              You're all caught up
            </Typography>
          </Box>
        )}

        {sorted.map((n) => (
          <MenuItem
            key={n.id}
            onClick={() => {
              if (n.link) navigate(n.link);
              setNotifAnchor(null);
            }}
            sx={{ py: 1.25, alignItems: 'flex-start', whiteSpace: 'normal' }}
          >
            <ListItemText
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  {/* Severity is a labelled chip, never a bare colour. */}
                  {(n.severity === 'CRITICAL' || n.severity === 'IMPORTANT') && (
                    <Chip
                      label={n.severity === 'CRITICAL' ? 'Critical' : 'Important'}
                      size="small"
                      color={n.severity === 'CRITICAL' ? 'error' : 'warning'}
                      sx={{ height: 17, fontSize: '0.625rem' }}
                    />
                  )}
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {n.title}
                    {n.count && n.count > 1 ? ` ×${n.count}` : ''}
                  </Typography>
                </Box>
              }
              secondary={n.body}
              secondaryTypographyProps={{ variant: 'caption' }}
            />
          </MenuItem>
        ))}
      </Menu>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      {assistantAvailable && (
        <AssistantPanel open={assistantOpen} onClose={() => setAssistantOpen(false)} />
      )}
    </>
  );
};

export default HeaderActions;
